import Keycloak from 'keycloak-js'

/**
 * Sign-in through Keycloak (auth.mentorfox.io) instead of the portal's own email-and-password form.
 * Off unless the build sets VITE_AUTH_MODE=keycloak, so demo bundles and local mock work keep their logins.
 */
export const keycloakAuth = import.meta.env.VITE_AUTH_MODE === 'keycloak'

/** Set while someone is signed in, so a reload restores the session but a first-time visitor is never sent to Keycloak just to look. */
const SIGNED_IN_HINT = 'mfox.signedIn'

let client: Keycloak | null = null
let started: Promise<boolean> | null = null

function instance(): Keycloak {
  client ??= new Keycloak({
    url: import.meta.env.VITE_KEYCLOAK_URL || 'https://auth.mentorfox.io',
    realm: import.meta.env.VITE_KEYCLOAK_REALM || 'mentorfox',
    clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'portal',
  })
  return client
}

function hint(value?: boolean): boolean {
  try {
    if (value === true) localStorage.setItem(SIGNED_IN_HINT, '1')
    else if (value === false) localStorage.removeItem(SIGNED_IN_HINT)
    return localStorage.getItem(SIGNED_IN_HINT) === '1'
  } catch {
    return false
  }
}

/** Finishes a sign-in the browser just came back from, or restores an earlier one. Resolves true when someone is signed in. Safe to call more than once. */
export function initKeycloak(): Promise<boolean> {
  started ??= (async () => {
    const kc = instance()
    const signedIn = await kc.init({
      // Without a hint, init only handles the return from a sign-in (code in the URL); it does not redirect.
      onLoad: hint() ? 'check-sso' : undefined,
      pkceMethod: 'S256',
      checkLoginIframe: false,
    })
    hint(signedIn)
    if (signedIn) {
      // The access token lives 15 minutes and requests read it without waiting, so renew it before it runs out.
      window.setInterval(() => {
        kc.updateToken(120).catch(endIfRefused)
      }, 60_000)
    }
    return signedIn
  })()
  return started
}

/** The current access token, or null when nobody is signed in. */
export function keycloakToken(): string | null {
  return client?.authenticated ? (client.token ?? null) : null
}

/** A token with at least 30 seconds left, renewed first if needed; null when the session is gone. */
export async function freshKeycloakToken(): Promise<string | null> {
  if (!client?.authenticated) return null
  try {
    await client.updateToken(30)
  } catch {
    endIfRefused()
    return null
  }
  return client.token ?? null
}

/**
 * A renewal can fail because Keycloak refused it (the session ended; keycloak-js then drops its tokens) or because
 * the network blinked (offline, a laptop waking up; the tokens stay). Only the first ends the session here.
 */
function endIfRefused(): void {
  if (!client?.authenticated) keycloakSessionLost()
}

/**
 * The Keycloak session is over (renewal refused, or the backend rejected its token): leave for the sign-in page
 * instead of looking signed in while every request fails. Forgetting the hint first matters: with it, the reload
 * would check the session, find it still alive at Keycloak and sign straight back in, and a backend that keeps
 * rejecting the token would then reload the page forever.
 */
export function keycloakSessionLost(): void {
  hint(false)
  window.location.assign('/login')
}

export function keycloakLogin(): Promise<void> {
  // Mark the browser before leaving, not after coming back: a page load in the first moments after sign-in would
  // otherwise not check for the session. If the person gives up at Keycloak, the next visit checks once and clears it.
  hint(true)
  return instance().login({ redirectUri: `${window.location.origin}/` })
}

export function keycloakLogout(): Promise<void> {
  hint(false)
  return instance().logout({ redirectUri: `${window.location.origin}/login` })
}
